import { useApp } from "./context";
import {
  Appearance,
  brands,
  defaultAppearance,
  layouts,
  Theme,
} from "./preferences";
import { Icon } from "./visual";

export function Brand({ footer = false }: { footer?: boolean }) {
  return (
    <a
      className="wordmark brand-contrasted"
      href="#/"
      aria-label="contrasted home"
    >
      <svg
        className="brand-mark"
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <g className="brand-ear ear-left">
          <path
            className="ear-outer"
            d="M19 5C8.5 5 3 13 3 24s5.5 19 16 19"
            strokeWidth="1.6"
          />
          <path d="M19 12c-6.5 0-10 5.2-10 12s3.5 12 10 12" strokeWidth="2.4" />
          <path d="M19 19c-2.8 0-4 2.1-4 5s1.2 5 4 5" strokeWidth="2.4" />
        </g>
        <g className="brand-ear ear-right">
          <path
            className="ear-outer"
            d="M29 5c10.5 0 16 8 16 19s-5.5 19-16 19"
            strokeWidth="1.6"
          />
          <path d="M29 12c6.5 0 10 5.2 10 12s-3.5 12-10 12" strokeWidth="2.4" />
          <path d="M29 19c2.8 0 4 2.1 4 5s-1.2 5-4 5" strokeWidth="2.4" />
        </g>
        <g className="brand-core">
          <circle cx="24" cy="24" r="3.5" strokeWidth="1.5" />
          <path
            d="M24 20.5a3.5 3.5 0 0 0 0 7Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M24 12v3M24 33v3" strokeWidth="1.5" />
        </g>
      </svg>
      <span>contrasted</span>
      {footer && <span className="sr-only">{brands.contrasted.note}</span>}
    </a>
  );
}
export function LayoutSelect() {
  const { theme, preference, t } = useApp();
  return (
    <label className="appearance-select">
      <span>{t("layout")}</span>
      <select
        value={theme}
        onChange={(e) => preference({ theme: e.target.value as Theme })}
      >
        {layouts.map((key) => (
          <option key={key} value={key}>
            {key[0].toUpperCase() + key.slice(1)}
          </option>
        ))}
      </select>
    </label>
  );
}
export function AppearanceControls() {
  const { appearance: a, preference, t, user } = useApp();
  const change = (values: Partial<Appearance>) =>
    preference({ appearance: values });
  return (
    <div className="appearance-controls">
      <p className="muted">{t(user ? "appearanceSaved" : "appearanceLocal")}</p>
      <div className="appearance-grid">
        <LayoutSelect />
        <label>
          <span>{t("fontFace")}</span>
          <select
            value={a.font}
            onChange={(e) =>
              change({ font: e.target.value as Appearance["font"] })
            }
          >
            <option value="editorial">{t("fontEditorial")}</option>
            <option value="sans">{t("fontSans")}</option>
            <option value="humanist">{t("fontHumanist")}</option>
            <option value="serif">{t("fontSerif")}</option>
            <option value="mono">{t("fontMono")}</option>
          </select>
        </label>
        <label>
          <span>{t("textSize")}</span>
          <select
            value={a.textSize}
            onChange={(e) =>
              change({ textSize: +e.target.value as Appearance["textSize"] })
            }
          >
            {[18, 20, 22, 24, 28].map((size) => (
              <option key={size} value={size}>
                {size} px
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t("palette")}</span>
          <select
            value={a.palette}
            onChange={(e) =>
              change({ palette: e.target.value as Appearance["palette"] })
            }
          >
            {(["theme", "linen", "ocean", "plum", "night"] as const).map(
              (key) => (
                <option key={key} value={key}>
                  {t(`palette_${key}`)}
                </option>
              ),
            )}
          </select>
        </label>
        <label>
          <span>{t("motion")}</span>
          <select
            value={a.motion}
            onChange={(e) =>
              change({ motion: e.target.value as Appearance["motion"] })
            }
          >
            <option value="system">{t("systemMotion")}</option>
            <option value="reduced">{t("reducedMotion")}</option>
          </select>
        </label>
      </div>
      <fieldset className="accessibility-options">
        <legend>{t("readingComfort")}</legend>
        {(["contrast", "spacing", "underline", "followPlayback"] as const).map(
          (key) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={a[key]}
                onChange={(e) => change({ [key]: e.target.checked })}
              />
              <span>{t(key)}</span>
            </label>
          ),
        )}
      </fieldset>
      <div className="reading-preview">
        <span className="eyebrow">{t("preview")}</span>
        <h3>{t("previewTitle")}</h3>
        <p>{t("intro")}</p>
        <a href="#/collection">
          {t("explore")} <Icon name="arrow" size={18} />
        </a>
      </div>
      <button
        className="button secondary"
        onClick={() => change({ ...defaultAppearance, brand: a.brand })}
      >
        {t("resetAppearance")}
      </button>
    </div>
  );
}
